import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminDeletePost, adminListPosts } from "@/lib/admin-blog.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/blog/")({
  component: AdminBlogList,
});

function AdminBlogList() {
  const list = useServerFn(adminListPosts);
  const del = useServerFn(adminDeletePost);
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"all" | "draft" | "published" | "scheduled">("all");
  const [search, setSearch] = useState("");

  const q = useQuery({
    queryKey: ["adminPosts", { page, status, search }],
    queryFn: () => list({ data: { page, status, search: search || undefined, pageSize: 25 } }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminPosts"] }),
  });

  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Posts</h1>
        <div className="flex gap-2">
          <Link to="/admin/pages"><Button variant="outline">All pages</Button></Link>
          <Link to="/admin/blog/new"><Button>New post</Button></Link>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-3">
        <div className="grow">
          <Input placeholder="Search title…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="w-48">
          <Select value={status} onValueChange={(v) => { setStatus(v as any); setPage(1); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Author</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {q.data?.posts.map((p: any) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-3 py-2">
                  <Link to="/admin/blog/$id" params={{ id: p.id }} className="font-medium text-foreground hover:underline">
                    {p.title}
                  </Link>
                  <div className="text-xs text-muted-foreground">/{p.slug}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${
                    p.status === "published" ? "bg-emerald-100 text-emerald-900" :
                    p.status === "scheduled" ? "bg-amber-100 text-amber-900" :
                    "bg-muted text-muted-foreground"
                  }`}>{p.status}</span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{p.author?.name ?? "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {p.status === "scheduled" && p.scheduled_at
                    ? `→ ${new Date(p.scheduled_at).toLocaleString()}`
                    : p.published_at
                    ? new Date(p.published_at).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => {
                      if (confirm(`Delete "${p.title}"? This cannot be undone.`)) deleteMut.mutate(p.id);
                    }}
                  >Delete</Button>
                </td>
              </tr>
            ))}
            {q.data && q.data.posts.length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No posts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>{total} posts</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</Button>
        </div>
      </div>
    </div>
  );
}